/**
 * Entity Rules Editor
 *
 * Settings page component for managing SWRL-like semantic rules per entity class.
 * Features:
 * - Tree view navigation by domain (Market/Business/Solution)
 * - Rule cards with category color-coding
 * - Enable/disable toggles
 * - Rule editor modal for custom rules
 */

import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  ChevronDown,
  Globe,
  Building2,
  Puzzle,
  Plus,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Zap,
  GitBranch,
  Tag,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  Pencil,
  X,
  Save,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { useAuthStore } from '@/stores/authStore'
import { entityRulesApi, type EntityRule, type GroupedRules } from '@/lib/api/client'
import { MARKET_CLASSES, BUSINESS_CLASSES, SOLUTION_CLASSES } from '@/lib/ontology/schema'

// ============================================================================
// CONSTANTS
// ============================================================================

const DOMAIN_CONFIG = {
  market: {
    color: '#5c8a6b',
    bg: '#5c8a6b',
    lightBg: 'rgba(92, 138, 107, 0.1)',
    label: 'Market Layer',
    icon: Globe,
  },
  business: {
    color: '#c9a227',
    bg: '#c9a227',
    lightBg: 'rgba(201, 162, 39, 0.1)',
    label: 'Business Layer',
    icon: Building2,
  },
  solution: {
    color: '#4a90a4',
    bg: '#4a90a4',
    lightBg: 'rgba(74, 144, 164, 0.1)',
    label: 'Solution Layer',
    icon: Puzzle,
  },
} as const

const CATEGORY_CONFIG = {
  validation: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.1)',
    icon: ShieldCheck,
    label: 'Validation',
    description: 'Ensures data completeness and correctness',
  },
  inference: {
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.1)',
    icon: Zap,
    label: 'Inference',
    description: 'Derives new knowledge from existing data',
  },
  classification: {
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.1)',
    icon: Tag,
    label: 'Classification',
    description: 'Categorizes entities based on conditions',
  },
  propagation: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
    icon: GitBranch,
    label: 'Propagation',
    description: 'Flows values through relationships',
  },
} as const

const SEVERITY_CONFIG = {
  error: { color: '#ef4444', icon: AlertTriangle, label: 'Error' },
  warning: { color: '#f59e0b', icon: AlertTriangle, label: 'Warning' },
  info: { color: '#3b82f6', icon: Info, label: 'Info' },
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function EntityRulesEditor() {
  const { currentAccount } = useAuthStore()
  const queryClient = useQueryClient()

  const [selectedEntity, setSelectedEntity] = useState<string | null>(null)
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set(['market', 'business', 'solution']))
  const [showEditor, setShowEditor] = useState(false)
  const [editingRule, setEditingRule] = useState<EntityRule | null>(null)

  // Fetch all rules for this account
  const { data, isLoading, error } = useQuery({
    queryKey: ['entityRules', currentAccount?.id],
    queryFn: async () => {
      if (!currentAccount?.id) return { rules: [], grouped: [] }
      return entityRulesApi.list(currentAccount.id)
    },
    enabled: !!currentAccount?.id,
    staleTime: 30000,
  })

  // Toggle rule enabled/disabled
  const toggleRuleMutation = useMutation({
    mutationFn: async ({ ruleId, isEnabled }: { ruleId: string; isEnabled: boolean }) => {
      if (!currentAccount?.id) throw new Error('No account')
      await entityRulesApi.update(currentAccount.id, ruleId, { isEnabled })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entityRules'] })
    },
  })

  // Delete rule
  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      if (!currentAccount?.id) throw new Error('No account')
      await entityRulesApi.delete(currentAccount.id, ruleId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entityRules'] })
    },
  })

  // Get rule counts per entity
  const ruleCountsByEntity = useMemo(() => {
    if (!data?.rules) return {}
    const counts: Record<string, number> = {}
    data.rules.forEach(rule => {
      counts[rule.entityClass] = (counts[rule.entityClass] || 0) + 1
    })
    return counts
  }, [data?.rules])

  // Get enabled rule counts per entity
  const enabledCountsByEntity = useMemo(() => {
    if (!data?.rules) return {}
    const counts: Record<string, number> = {}
    data.rules.forEach(rule => {
      if (rule.isEnabled) {
        counts[rule.entityClass] = (counts[rule.entityClass] || 0) + 1
      }
    })
    return counts
  }, [data?.rules])

  // Get rules for selected entity
  const selectedRules = useMemo(() => {
    if (!selectedEntity || !data?.rules) return []
    return data.rules.filter(r => r.entityClass === selectedEntity)
  }, [selectedEntity, data?.rules])

  // Toggle domain expansion
  const toggleDomain = (domain: string) => {
    const newExpanded = new Set(expandedDomains)
    if (newExpanded.has(domain)) {
      newExpanded.delete(domain)
    } else {
      newExpanded.add(domain)
    }
    setExpandedDomains(newExpanded)
  }

  // Get entity classes by domain from ontology schema
  const entitiesByDomain = useMemo(() => ({
    market: MARKET_CLASSES.map(c => c.localName),
    business: BUSINESS_CLASSES.map(c => c.localName),
    solution: SOLUTION_CLASSES.map(c => c.localName),
  }), [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-warm-500" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-solution">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p>Failed to load entity rules</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-market to-solution flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base font-semibold">Semantic Validation Rules</CardTitle>
              <CardDescription>
                Configure rules for entity validation, inference, and classification
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              {data?.rules.length || 0} rules • {data?.rules.filter(r => r.isEnabled).length || 0} active
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content - Tree + Rules */}
      <div className="grid grid-cols-[280px_1fr] gap-6">
        {/* Entity Tree */}
        <Card className="h-fit max-h-[600px] overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-y-auto max-h-[580px]">
              {(['market', 'business', 'solution'] as const).map(domain => {
                const config = DOMAIN_CONFIG[domain]
                const Icon = config.icon
                const isExpanded = expandedDomains.has(domain)
                const entities = entitiesByDomain[domain]

                return (
                  <div key={domain}>
                    {/* Domain Header */}
                    <button
                      onClick={() => toggleDomain(domain)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-warm-50 transition-colors border-b border-warm-100"
                      style={{ backgroundColor: isExpanded ? config.lightBg : undefined }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-warm-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-warm-500" />
                      )}
                      <Icon className="w-4 h-4" style={{ color: config.color }} />
                      <span className="text-sm font-medium flex-1 text-left" style={{ color: config.color }}>
                        {config.label}
                      </span>
                      <span className="text-xs text-warm-500">
                        {entities.filter(e => ruleCountsByEntity[e]).length}/{entities.length}
                      </span>
                    </button>

                    {/* Entity List */}
                    {isExpanded && (
                      <div className="py-1">
                        {entities.map(entity => {
                          const ruleCount = ruleCountsByEntity[entity] || 0
                          const enabledCount = enabledCountsByEntity[entity] || 0
                          const isSelected = selectedEntity === entity

                          return (
                            <button
                              key={entity}
                              onClick={() => setSelectedEntity(entity)}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm transition-colors ${
                                isSelected
                                  ? 'bg-warm-100 text-warm-900'
                                  : 'hover:bg-warm-50 text-warm-700'
                              }`}
                            >
                              <span className="pl-4 flex-1 truncate">{entity}</span>
                              {ruleCount > 0 && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  enabledCount === ruleCount
                                    ? 'bg-business/10 text-business'
                                    : enabledCount > 0
                                    ? 'bg-warm-200 text-warm-600'
                                    : 'bg-warm-100 text-warm-400'
                                }`}>
                                  {enabledCount}/{ruleCount}
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Rules Panel */}
        <div className="space-y-4">
          {selectedEntity ? (
            <>
              {/* Entity Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-warm-900">{selectedEntity}</h3>
                  <p className="text-sm text-warm-500">
                    {selectedRules.length} rules configured
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingRule(null)
                    setShowEditor(true)
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Rule
                </Button>
              </div>

              {/* Rules List */}
              {selectedRules.length > 0 ? (
                <div className="space-y-3">
                  {selectedRules.map(rule => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      onToggle={(isEnabled) => toggleRuleMutation.mutate({ ruleId: rule.id, isEnabled })}
                      onEdit={() => {
                        setEditingRule(rule)
                        setShowEditor(true)
                      }}
                      onDelete={() => deleteRuleMutation.mutate(rule.id)}
                      isToggling={toggleRuleMutation.isPending}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-center text-warm-500">
                      <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No rules configured for {selectedEntity}</p>
                      <p className="text-sm mt-1">Click "Add Rule" to create validation or inference rules</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-warm-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">Select an entity class</p>
                  <p className="text-sm mt-1">Choose from the tree view to see and manage its rules</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Rule Editor Modal */}
      {showEditor && (
        <RuleEditorModal
          rule={editingRule}
          entityClass={selectedEntity || ''}
          accountId={currentAccount?.id || ''}
          onClose={() => {
            setShowEditor(false)
            setEditingRule(null)
          }}
          onSaved={() => {
            setShowEditor(false)
            setEditingRule(null)
            queryClient.invalidateQueries({ queryKey: ['entityRules'] })
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// RULE CARD COMPONENT
// ============================================================================

interface RuleCardProps {
  rule: EntityRule
  onToggle: (isEnabled: boolean) => void
  onEdit: () => void
  onDelete: () => void
  isToggling: boolean
}

function RuleCard({ rule, onToggle, onEdit, onDelete, isToggling }: RuleCardProps) {
  const categoryConfig = CATEGORY_CONFIG[rule.category]
  const severityConfig = SEVERITY_CONFIG[rule.severity]
  const CategoryIcon = categoryConfig.icon
  const [showDetails, setShowDetails] = useState(false)

  return (
    <Card className={`transition-all ${!rule.isEnabled ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Category Icon */}
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: categoryConfig.bg }}
          >
            <CategoryIcon className="w-5 h-5" style={{ color: categoryConfig.color }} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-warm-900">{rule.name}</span>
              {rule.isSystemRule && (
                <Badge variant="outline" className="text-xs">System</Badge>
              )}
              <Badge
                variant="outline"
                className="text-xs"
                style={{ color: severityConfig.color, borderColor: severityConfig.color }}
              >
                {rule.severity}
              </Badge>
            </div>

            {rule.description && (
              <p className="text-sm text-warm-600 mb-2">{rule.description}</p>
            )}

            {/* Rule Logic */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs text-warm-500 hover:text-warm-700 flex items-center gap-1"
            >
              {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {showDetails ? 'Hide details' : 'Show rule logic'}
            </button>

            {showDetails && (
              <div className="mt-3 p-3 bg-warm-50 rounded-lg text-xs font-mono space-y-2">
                <div>
                  <span className="text-warm-500">IF: </span>
                  <span className="text-warm-800">{rule.antecedent}</span>
                </div>
                <div>
                  <span className="text-warm-500">THEN: </span>
                  <span className="text-warm-800">{rule.consequent}</span>
                </div>
                {rule.example && (
                  <div className="pt-2 border-t border-warm-200">
                    <span className="text-warm-500">Example: </span>
                    <span className="text-warm-600 italic">{rule.example}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {!rule.isSystemRule && (
              <>
                <Button variant="ghost" size="sm" onClick={onEdit} className="h-8 w-8 p-0">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="h-8 w-8 p-0 text-solution hover:text-solution"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            <Switch
              checked={rule.isEnabled}
              onCheckedChange={onToggle}
              disabled={isToggling}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// RULE EDITOR MODAL
// ============================================================================

interface RuleEditorModalProps {
  rule: EntityRule | null
  entityClass: string
  accountId: string
  onClose: () => void
  onSaved: () => void
}

function RuleEditorModal({ rule, entityClass, accountId, onClose, onSaved }: RuleEditorModalProps) {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    category: rule?.category || 'validation' as const,
    antecedent: rule?.antecedent || '',
    consequent: rule?.consequent || '',
    severity: rule?.severity || 'warning' as const,
    example: rule?.example || '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Determine entity domain from schema
  const entityDomain = useMemo(() => {
    if (MARKET_CLASSES.some(c => c.localName === entityClass)) return 'market'
    if (BUSINESS_CLASSES.some(c => c.localName === entityClass)) return 'business'
    if (SOLUTION_CLASSES.some(c => c.localName === entityClass)) return 'solution'
    return 'market'
  }, [entityClass])

  const handleSave = async () => {
    if (!formData.name || !formData.antecedent || !formData.consequent) {
      setError('Name, antecedent, and consequent are required')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      if (rule) {
        // Update existing
        await entityRulesApi.update(accountId, rule.id, formData)
      } else {
        // Create new
        await entityRulesApi.create(accountId, {
          ...formData,
          entityClass,
          entityDomain,
        })
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-warm-200">
          <h2 className="text-lg font-semibold">
            {rule ? 'Edit Rule' : 'New Rule'} for {entityClass}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-solution/10 border border-solution/20 rounded-lg text-sm text-solution">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Rule Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-warm-400"
              placeholder="e.g., Must Have Severity"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-warm-400 resize-none"
              rows={2}
              placeholder="What does this rule check?"
            />
          </div>

          {/* Category & Severity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as typeof formData.category })}
                className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-warm-400"
              >
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value as typeof formData.severity })}
                className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-warm-400"
              >
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>

          {/* Antecedent (IF) */}
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Antecedent (IF) *</label>
            <textarea
              value={formData.antecedent}
              onChange={(e) => setFormData({ ...formData, antecedent: e.target.value })}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-warm-400 resize-none"
              rows={2}
              placeholder="e.g., CustomerPain(?pain) AND hasSeverity(?pain) = NULL"
            />
          </div>

          {/* Consequent (THEN) */}
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Consequent (THEN) *</label>
            <textarea
              value={formData.consequent}
              onChange={(e) => setFormData({ ...formData, consequent: e.target.value })}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-warm-400 resize-none"
              rows={2}
              placeholder="e.g., mark as IncompletePain"
            />
          </div>

          {/* Example */}
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Example</label>
            <input
              type="text"
              value={formData.example}
              onChange={(e) => setFormData({ ...formData, example: e.target.value })}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-warm-400"
              placeholder="e.g., Pain 'Slow invoicing' without severity level is flagged"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-warm-200 bg-warm-50">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Save Rule
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
